import React, { useState, useEffect } from 'react';
import { Table, message } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ICommonCode, IProject } from 'utils/types/storyTypes';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import DefaultButton from 'components/button/DefaultButton';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { handleMessage } from 'utils/helper/message';
import ActionButton from 'components/table/ActionButton';
import {
  AddSquare,
  ArrowDown2,
  ArrowUp2,
  Edit2,
  PlayCricle,
  Trash,
} from 'iconsax-react';
import SelectMultiple from '../_component/SearchMultiple';

import { characterApis } from 'apis/resource/characterApis';
import CreateModal from '../_component/Live/CreateModal';
import DeleteModal from 'components/modal/DeleteModal';
import UploadModal from '../_component/Live/UploadModal';
import { liveObjApi } from 'apis/resource/liveObjectApis';
import CheckboxStyled from 'components/CheckboxStyled';
import {
  LIVE2D_DOWNLOADER_URL,
  LIVE2D_VIEWER_URL,
  ifyouApi,
} from 'apis/config';

/**
 * * 리소스 - 배경 목록
 */

interface IProps {
  selectProject: IProject;
}

const LiveObjectTable = ({ selectProject }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams(); // id: 프로젝트 id
  const [searchData, setSearchData] = useState<string[]>([]); // 검색어
  const [visible, setVisible] = useState({
    register: false,
    delete: false,
    zip: false,
    name: '',
  }); // 모달 show on/off
  const [selectedData, setSelectedData] = useState<any>(null);

  const [charactersData, setCharacterData] = useState<ICommonCode[] | null>(
    null,
  );
  const [selectCharacterData, setSelectCharacterData] = useState<
    ICommonCode[] | null
  >(null);

  useEffect(() => {
    if (charactersData) {
      setSelectCharacterData(charactersData);
    }
  }, [charactersData]);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleFetchData = async () => {
    if (projectId) {
      try {
        const res = await liveObjApi.liveObjectList(projectId);
        setData(res.list);
        const characterRes = await characterApis.list(projectId.toString());
        setCharacterData(characterRes);
      } catch (err) {
        console.log('fetch data error: ', err);
      }
    }
  };

  useEffect(() => {
    if (data) {
      setTableData(data);
    }
  }, [data]);

  useEffect(() => {
    handleFetchData();
  }, [refresh]);

  // 배경 삭제
  const handleClickDeleteModal = (record) => {
    setSelectedData(record);
    setVisible({
      ...visible,
      delete: true,
      name: record?.live_name,
    });
  };

  // * 모션 클릭
  const handleClickMotion = async (record) => {
    console.log(record);

    if (!record.details) {
      handleMessage('파일이 등록되지 않음', 'Error', '파일이 등록되지 않음');
      return;
    }

    const downloadResult = await ifyouApi.post(LIVE2D_DOWNLOADER_URL, {
      list: record.details,
      isCharacter: false,
    });

    if (downloadResult.status != 200) {
      handleMessage('Download Fail', 'Error', 'Please try again');
      return;
    }

    console.log('download result : ', downloadResult.data);

    let newFileKey;
    record.details?.map((v) => {
      if (v.file_key?.indexOf('.model3.json') !== -1) {
        newFileKey = v;
        return newFileKey;
      }

      return null;
    });

    console.log(`model key : `, newFileKey);
    if (!newFileKey) {
      handleMessage('No Main File in Live2D', 'Error', 'Check LiveObject');
      return;
    }

    const motionView = `${LIVE2D_VIEWER_URL}?modelid=${record.model_id}&modelinfo=${newFileKey.file_key}`;
    console.log(`view url : `, motionView);
    window.open(motionView, '_blank'); //motionview 새탭열기
  };

  const handleDelete = async () => {
    if (selectedData) {
      try {
        setLoading(true);
        await liveObjApi.liveObjectDelete(projectId, selectedData.id);
        setRefresh(!refresh);
        setLoading(false);
        setVisible({
          ...visible,
          delete: false,
          name: selectedData?.live_name,
        });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  const handleLink = (record) => {
    navigate(`${record.id}/update`, {
      state: {
        record: record,
        project_type: location?.state?.record?.project_type,
      },
    });
  };

  const expandedRowRender = (record) => {
    return (
      <MainTable
        columns={columnsNested}
        dataSource={record.details}
        isPagination={false}
      />
    );
  };

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <AddSquare
            size='24'
            onClick={() => {
              setSelectedData(record);
              setVisible({ ...visible, zip: true });
            }}
          />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <PlayCricle
            size='24'
            onClick={() => {
              handleClickMotion(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },
      {
        key: 3,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              handleLink(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 4,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.name',
      ),
      dataIndex: 'live_name',
      width: '9rem',
      sorter: (a, b) => {
        if (a.live_name && b.live_name) {
          return a.live_name.localeCompare(b.live_name, undefined, {
            sensitivity: 'base',
          });
        } else if (a.live_name && !b.live_name) {
          return -1;
        } else if (!a.live_name && b.live_name) {
          return 1;
        } else {
          return 0;
        }
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.character',
      ),
      dataIndex: 'speaker',
      sorter: (a, b) => {
        if (a.speaker && b.speaker) {
          return a.speaker.localeCompare(b.speaker, undefined, {
            sensitivity: 'base',
          });
        } else if (a.speaker && !b.speaker) {
          return -1;
        } else if (!a.speaker && b.speaker) {
          return 1;
        } else {
          return 0;
        }
      },
      width: '7rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.version',
      ),
      dataIndex: 'resource_ver',
      width: '7rem',
    },
    {
      title: 'X Offset',
      dataIndex: 'offset_x',
      width: '7rem',
    },
    {
      title: 'Y Offset',
      dataIndex: 'offset_y',
      width: '7rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.scale',
      ),
      dataIndex: 'game_scale',
      width: '7rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.liveTable.status',
      ),
      dataIndex: 'is_public',
      width: '7rem',
      render: (_) => {
        return <CheckboxStyled isChecked={_} />;
      },
    },
    {
      title: '',
      dataIndex: '',
      width: '4rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
    Table.EXPAND_COLUMN,
  ];

  const columnsNested = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.table.assetName',
      ),
      dataIndex: 'file_name',
      width: '9rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.table.motion',
      ),
      dataIndex: 'motion_name',
      width: '7rem',
    },
  ];

  // handle search input onChange
  const handleSelectChange = (value: any, data: any) => {
    setSearchData(value);
  };
  useEffect(() => {
    const cloneData = JSON.parse(JSON.stringify(data));
    if (cloneData) {
      if (searchData.length) {
        const newArray = cloneData.filter((obj) =>
          searchData.includes(obj.speaker),
        );
        setTableData(newArray);
      } else {
        setTableData(data);
      }
    }
  }, [searchData]);

  return (
    <>
      <TableToolbar>
        <SelectMultiple
          optionList={selectCharacterData?.map((e) => {
            return {
              value: e.code,
              label: e.code_name,
            };
          })}
          placeholder='Search...'
          mode='tags'
          onChange={handleSelectChange}
          width='320px'
          value={searchData}
          setValue={setSearchData}
        />
        <div>
          <DefaultButton
            name='register'
            onClick={() => setVisible({ ...visible, register: true })}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.createModal.title',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      <TableWrapper>
        <MainTable
          rowKey='id'
          rowClassName={(record) => {
            if (record && record.id) {
              if (expandedRows.includes(record.id)) {
                return 'active';
              }
            }
          }}
          isExpand
          columns={columnsOrigin}
          dataSource={tableData}
          expandable={{
            expandedRowRender: (record) => {
              return expandedRowRender(record);
            },
            expandIcon: ({ expanded, onExpand, record }) => {
              // Customize the expand column icon based on the 'expanded' state
              return expanded ? (
                <ArrowUp2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <ArrowDown2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              );
            },
            onExpandedRowsChange: (expandedRows) => {
              setExpandedRows(expandedRows);
            },
          }}
        />
      </TableWrapper>

      <CreateModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        data={selectedData}
        type='live_object'
        projectId={projectId}
      />

      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => {
          setVisible({ ...visible, delete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.deleteModal.modalTitle',
        )}
        content='storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.live.deleteModal.content'
        label={visible.name}
        handleDelete={handleDelete}
        loading={loading}
      />

      <UploadModal
        visible={visible}
        setVisible={(e) => {
          setVisible({ ...visible, zip: e });
        }}
        refresh={refresh}
        setRefresh={setRefresh}
        data={selectedData}
        type='live_object'
        projectId={projectId}
      />
    </>
  );
};

export default LiveObjectTable;

const TableWrapper = styled.div`
  .ant-table table tr {
    vertical-align: top;
  }
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-table-thead > tr > th,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr
    > td
    > .ant-table-wrapper:only-child
    .ant-table {
    margin: 0 10px;
  }
`;
